import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Users } from "lucide-react";
import TopBar from "@/components/TopBar";
import { useLanguage } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import { CATEGORIES, categoryLabel } from "@/lib/categories";

const FALLBACK_IMG =
  "https://images.pexels.com/photos/5872179/pexels-photo-5872179.jpeg?auto=compress&cs=tinysrgb&w=900";

export default function Shop() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const collectiveMode = params.get("collective") === "1";
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [category, setCategory] = useState("all");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = category === "all" ? "" : `&category=${category}`;
    api.get(`/public/products?special=false${q}`)
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [category]);

  return (
    <div className="min-h-screen bg-[#FFF9FB] pb-24">
      <TopBar />
      <div className="max-w-6xl mx-auto px-5">
        <button
          onClick={() => navigate(`/event/${code}`)}
          data-testid="shop-back"
          className="inline-flex items-center gap-2 text-sm text-[#5C4664] hover:text-[#2A0E3C]"
        >
          <ArrowLeft className="w-4 h-4" /> {t("product.backToEvent")}
        </button>

        <div className="mt-6">
          <h1 className="font-display text-3xl sm:text-4xl font-black text-[#2A0E3C]">
            {t("shop.title")}
          </h1>
          <p className="mt-2 text-[#5C4664]">{t("shop.sub")}</p>

          {collectiveMode && (
            <div
              data-testid="collective-banner"
              className="mt-5 p-4 rounded-2xl flex items-start gap-3"
              style={{
                background: "linear-gradient(135deg, #E8F6E5 0%, #EFE3F5 100%)",
              }}
            >
              <Users className="w-5 h-5 text-[#3FA535] mt-0.5 shrink-0" />
              <p className="text-sm text-[#2A0E3C]">
                <strong>Mode cadeau collectif :</strong> choisissez le cadeau que votre groupe souhaite réserver.
              </p>
            </div>
          )}
        </div>

        {/* Category chips */}
        <div className="mt-7 -mx-5 px-5 overflow-x-auto pb-1">
          <div className="flex gap-2 sm:gap-3 min-w-max">
            <CategoryChip
              active={category === "all"}
              onClick={() => setCategory("all")}
              label={t("shop.allCategories")}
              testId="cat-chip-all"
            />
            {CATEGORIES.map((c) => (
              <CategoryChip
                key={c.slug}
                active={category === c.slug}
                onClick={() => setCategory(c.slug)}
                label={categoryLabel(c.slug, lang)}
                Icon={c.icon}
                color={c.color}
                testId={`cat-chip-${c.slug}`}
              />
            ))}
          </div>
        </div>

        {/* Product grid */}
        <div className="mt-8">
          {loading ? (
            <div className="py-16 text-center text-[#5C4664]">{t("common.loading")}</div>
          ) : products.length === 0 ? (
            <div data-testid="shop-empty" className="py-16 text-center text-[#5C4664]">
              {t("shop.noProducts")}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {products.map((p) => (
                <Link
                  key={p.id}
                  to={`/event/${code}/shop/product/${p.id}${collectiveMode ? "?collective=1" : ""}`}
                  data-testid={`product-card-${p.id}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-[#F2E2EC] hover:border-[#D81B8C]/50 hover:shadow-lg transition-all"
                >
                  <div className="aspect-square w-full bg-[#FCE7F3] overflow-hidden">
                    <img
                      src={p.image_url || FALLBACK_IMG}
                      alt={p.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-3 sm:p-4">
                    <div className="text-[9px] font-bold uppercase tracking-widest text-[#5C4664]">
                      {categoryLabel(p.category, lang)}
                    </div>
                    <h3 className="mt-1 font-display text-xs sm:text-sm font-black text-[#2A0E3C] leading-snug line-clamp-2">
                      {p.name}
                    </h3>
                    {p.description && (
                      <p className="mt-1 text-[10px] text-[#5C4664] line-clamp-2 hidden sm:block">
                        {p.description}
                      </p>
                    )}
                    <div className="mt-2 font-display text-base sm:text-lg font-black text-[#D81B8C]">
                      ${(p.final_price ?? p.price).toFixed(0)}
                    </div>
                    <div className="text-[9px] text-[#5C4664]">{t("common2.allInclusive")}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CategoryChip({ active, onClick, label, Icon, color, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-2 px-4 h-10 rounded-full font-semibold text-sm whitespace-nowrap transition-all ${
        active
          ? "bg-[#2A0E3C] text-white"
          : "bg-white text-[#2A0E3C] border border-[#F2E2EC] hover:border-[#D81B8C]/40"
      }`}
    >
      {Icon ? (
        <Icon
          className="w-4 h-4"
          style={{ color: active ? "#fff" : color }}
        />
      ) : null}
      {label}
    </button>
  );
}
